import type { Issue } from '../../../../api/DigiobcanApi';
import DigiobcanApi from '../../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import FormTextField from '../../../../components/form/FormTextField';
import { adminGetReportIssues } from '../../../../features/queries/adminGetReportIssues';
import { entityId } from '../../../../lib/ids';
import { useM, useQ } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import { __, noSpaces, toastError, toastSuccess } from '../../../../utils';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, Button } from '@presov.sk/presov-ui-react';
import React from 'react';
import { Form } from 'react-final-form';

type Props = {
  issue: Issue;
  /** Obnova obálky aj nálezov - po prijatí späť sa mení oboje. */
  onDone?: () => Promise<unknown>;
  /**
   * Podnet je archivovaný. Nesie to stránka: `Issue` o archivácii nevie a je
   * to príznak podnetu, nie nálezu. Rozhodovanie o náleze zhasína
   * `buildDecision`, ale tento pruh stojí mimo neho (vykresľuje sa podľa
   * `status === 'moved'`), takže by inak zostal aktívny.
   */
  archived?: boolean;
  /**
   * Nález leží v kategórii, ktorú úradník len vidí
   * (`pruser:report-categories:read-all`). Znovu prijať ho nemôže - je to
   * zápis cez `adminUpdateReportStatus` a ten skončí 403. Rovnaký dôvod, prečo
   * tu stojí `archived`: pruh je mimo `buildDecision`, takže by inak zostal
   * aktívny.
   */
  readOnly?: boolean;
};

/**
 * „Znovu prijať nález" - kategória si berie podnet späť.
 *
 * Vznikne v nej **nový nález priamo v stave V riešení** a ten, ktorého
 * identifikátor sa zadá, prejde do Presunuté. To je celý zmysel toho poľa:
 * prijatie berie prácu inej kategórii, takže ten, komu pribudne, ju musí
 * vedome pomenovať - a nadiktuje mu ju kolega, ktorý nález práve drží. Preto
 * to nie je chýbajúce pole ani vec, ktorú by šlo dopočítať; je to ochrana pred
 * slučkou, v ktorej si dve kategórie podnet prehadzujú dokola.
 *
 * Dizajnový balík túto akciu nekreslí, ale je to jediná cesta späť z `moved`
 * a stojí na `current_issue_id` v `adminUpdateReportStatus`, ktorý preto
 * zostáva vedľa nového `adminIssueDecision`. Zrušiť ju spolu s prekreslením
 * obrazovky by bola tichá strata funkcie, nie zjednodušenie.
 */
const IssueRetake: React.FC<Props> = ({
  issue,
  onDone,
  archived = false,
  readOnly = false,
}) => {
  const { logger } = useLogger();

  const [retakeDialogOpen, setRetakeDialogOpen] = React.useState(false);

  const { refetch: refetchIssues } = useQ(adminGetReportIssues, {
    reportId: issue.report_id,
  });

  const retakeIssueMutation = useM(
    (currentIssueId: string) => {
      return DigiobcanApi.adminUpdateReportStatus(issue.report_id, undefined, {
        category_id: issue.category_id,
        status: 'accepted',
        current_issue_id: currentIssueId,
      });
    },
    {
      onSuccess: async () => {
        await (onDone ? onDone() : refetchIssues());
        setRetakeDialogOpen(false);
        toastSuccess(__('Nález bol prijatý do riešenia'));
      },
      onError: (e, variables) => {
        logger.error('Error retaking issue', {
          action: 'retakeIssue',
          error: e,
          data: {
            category_id: issue.category_id,
            status: 'accepted',
            current_issue_id: variables,
          },
        });

        if (e.details.name === 'INVALID_CURRENT_ISSUE_ID') {
          toastError(__('Jedinečný identifikátor je neplatný'));
        } else if (e.details.name === 'ISSUE_ALREADY_ACCEPTED') {
          toastError(__('Podnet s týmto identifikátorom je už akceptovaný.'));
        } else {
          toastError(__('Podnet sa nepodarilo opätovne prijať'));
        }

        setRetakeDialogOpen(false);
      },
    },
  );

  return (
    <>
      <Button
        variant='outline'
        size='small'
        endIcon={<UndoIcon fontSize='small' />}
        fullWidth
        disabled={archived || readOnly}
        onClick={() => {
          setRetakeDialogOpen(true);
        }}
      >
        {__('Znovu prijať nález')}
      </Button>

      <Form
        key={retakeDialogOpen ? 'true' : 'false'}
        onSubmit={async (values) => {
          if (!navigator.onLine) {
            toastError(__('Nie ste pripojený k internetu.'));
            return;
          }
          try {
            // Kolega môže nadiktovať aj samotný koncový segment - tak je
            // nález napísaný v adrese aj v stĺpci ID. `entityId` doplní
            // predponu, ktorú čaká API.
            await retakeIssueMutation.mutateAsync(
              entityId(String(values.issueID).trim(), 'issue'),
            );
          } catch {
            // Empty catch because mutation handles errors
          }
        }}
        initialValues={{ issueID: '' }}
      >
        {({ handleSubmit, submitting }) => (
          <ConfirmationDialog
            title={__('Znovu prijať nález')}
            description={__(
              `Nález sa vráti do kategórie ${issue.category_name} priamo v stave V riešení. Zadajte identifikátor nálezu, ktorý vám nadiktoval kolega - ten, čo podnet drží teraz. Prejde do stavu Presunuté.`,
            )}
            open={retakeDialogOpen}
            onClose={() => {
              setRetakeDialogOpen(false);
            }}
            confirmLabel={__('Prijať do riešenia')}
            isLoading={submitting}
            formId='retakeIssueForm'
          >
            <form onSubmit={handleSubmit} id='retakeIssueForm'>
              <Box
                sx={{
                  mt: 1,
                }}
              >
                <FormTextField
                  name='issueID'
                  label={__('Identifikátor nálezu od kolegu')}
                  required
                  validate={noSpaces}
                  fullWidth
                />
              </Box>
            </form>
          </ConfirmationDialog>
        )}
      </Form>
    </>
  );
};

export default IssueRetake;
